import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core'
import { Auth } from '@angular/fire/auth'
import { MatIcon } from '@angular/material/icon'
import { Title } from '@angular/platform-browser'
import { Router } from '@angular/router'
import { lastValueFrom } from 'rxjs'
import { ButtonComponent } from '../../components/button/button.component'
import {
  AuthService,
  PasskeyClaimMissingError,
} from '../../services/auth.service'
import { PasskeyService } from '../../services/passkey/passkey.service'
import { WEBAUTHN_CLIENT } from '../../services/passkey/webauthn-client'
import {
  passkeyErrorMessage,
  passkeyAuthenticationError,
} from '../../services/passkey/passkey-error'
import { getTitle } from '../../util/accessibility'

@Component({
  selector: 'app-passkey-verify-page',
  standalone: true,
  imports: [MatIcon, ButtonComponent],
  templateUrl: './passkey-verify-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './passkey-verify-page.component.scss',
})
export class PasskeyVerifyPageComponent implements OnInit {
  private auth = inject(Auth)
  private authService = inject(AuthService)
  private passkeys = inject(PasskeyService)
  private webauthn = inject(WEBAUTHN_CLIENT)
  private router = inject(Router)
  supported = this.webauthn.browserSupportsWebAuthn()
  busy = false
  errorMessage = ''
  canRetry = true
  constructor() {
    inject(Title).setTitle(getTitle('パスキーの認証'))
  }
  async ngOnInit(): Promise<void> {
    await this.authService.waitForAuthStateReady()
    if (!this.auth.currentUser) await this.router.navigate(['/login'])
  }
  async submit(): Promise<void> {
    if (this.busy || !this.supported || !this.canRetry) return
    if (!this.auth.currentUser) {
      await this.router.navigate(['/login'])
      return
    }
    this.busy = true
    this.errorMessage = ''
    try {
      const options = await lastValueFrom(this.passkeys.beginAuthentication())
      const response = await this.webauthn.startAuthentication({
        optionsJSON: JSON.parse(options.options),
      })
      const result = await lastValueFrom(
        this.passkeys.finishAuthentication(
          options.challengeId,
          JSON.stringify(response),
        ),
      )
      await this.authService.signInWithCustomToken(result.customToken)
      const returnUrl = this.authService.consumeReturnUrl() || '/home'
      this.authService.clearLoginState()
      await this.router.navigateByUrl(returnUrl)
    } catch (error) {
      if (error instanceof PasskeyClaimMissingError) {
        this.canRetry = false
        return
      }
      const authenticationError = passkeyAuthenticationError(error)
      if (authenticationError) {
        this.canRetry = false
        if (authenticationError === 'MFA_REQUIRED') {
          this.errorMessage =
            '認証状態を確認できませんでした。ログインからやり直してください。'
        }
        // その他の認証エラーの遷移は ErrorLink が行う。
        return
      }
      this.canRetry = !(
        error instanceof Error &&
        'code' in error &&
        error.code === 'PASSKEY_NOT_ALLOWED'
      )
      this.errorMessage = passkeyErrorMessage(error)
    } finally {
      this.busy = false
    }
  }
  async back(): Promise<void> {
    await this.authService.signOutPreservingReturnUrl()
    this.authService.redirectToLogin()
  }
}
